import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import CollapsibleSection from './CollapsibleSection';
import '../styles/previous-rehearsal-notes.css';

const PreviousRehearsalNotes = () => {
  const { user } = useAuth();
  const { t } = useLanguage();

  const [rehearsalNotes, setRehearsalNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadRehearsalNotes();
  }, []);

  const loadRehearsalNotes = async () => {
    setLoading(true);
    try {
      // Get last 6 months of data
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const startDate = sixMonthsAgo.toISOString().split('T')[0];
      const endDate = new Date().toISOString().split('T')[0];

      const response = await fetch(`/api/calendar?operation=getEntries&startDate=${startDate}&endDate=${endDate}`);
      if (response.ok) {
        const allEntries = await response.json();
        
        // Filter for rehearsal notes and sort by date (newest first)
        const notes = allEntries
          .filter(entry => entry.entry_type === 'rehearsal_notes')
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        
        console.log('Loaded rehearsal notes:', notes);
        setRehearsalNotes(notes);
      } else {
        console.error('Failed to load rehearsal notes:', response.status);
      }
    } catch (error) {
      console.error('Error loading rehearsal notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleNoteExpansion = (noteId) => {
    const newExpanded = new Set(expandedNotes);
    if (newExpanded.has(noteId)) {
      newExpanded.delete(noteId);
    } else {
      newExpanded.add(noteId);
    }
    setExpandedNotes(newExpanded);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleAudioPlay = (clipId) => {
    if (playingAudio === clipId) {
      // Stop current audio
      setPlayingAudio(null);
    } else {
      // Start new audio
      setPlayingAudio(clipId);
    }
  };

  const handleAudioDownload = async (clip) => {
    try {
      const response = await fetch(clip.file_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Try to get the original filename, but change extension to reflect actual format
      const originalName = clip.filename || clip.name || 'audio-clip';
      const nameWithoutExt = originalName.replace(/\.(webm|mp3|wav|m4a)$/i, '');
      a.download = `${nameWithoutExt}.webm`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download audio:', error);
      window.open(clip.file_url, '_blank');
    }
  };

  // Determine which notes to display
  const displayedNotes = showAll ? rehearsalNotes : rehearsalNotes.slice(0, 3);
  const hasMoreNotes = rehearsalNotes.length > 3;

  return (
    <div className="previous-rehearsal-notes-section" style={{
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      border: '1px solid #374151',
      overflow: 'hidden',
      marginBottom: '20px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        color: '#ffffff',
        padding: '14px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>📝</span>
          <h3 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '600'
          }}>
            Previous Rehearsal Notes
          </h3>
          {!loading && rehearsalNotes.length > 0 && (
            <span style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.8)',
              fontWeight: '500',
              marginLeft: '8px'
            }}>
              ({rehearsalNotes.length} total)
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="previous-rehearsal-notes-container" style={{ padding: '20px' }}>
        {loading && (
          <div className="notes-loading" style={{
            textAlign: 'center',
            padding: '20px',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            Loading rehearsal notes...
          </div>
        )}

        {!loading && rehearsalNotes.length === 0 && (
          <div className="no-notes" style={{
            textAlign: 'center',
            padding: '20px',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            No rehearsal notes found in the last 6 months.
          </div>
        )}

        {!loading && rehearsalNotes.length > 0 && (
          <>
            <div className="notes-list">
              {displayedNotes.map((note) => (
              <div key={note.id} className="note-item">
                <div className="note-header" onClick={() => toggleNoteExpansion(note.id)}>
                  <div className="note-title-row">
                    <span className="note-title">{note.title}</span>
                    <span className="note-date">{formatDate(note.date)}</span>
                  </div>
                  <div className="note-expand-toggle">
                    <span className={`expand-arrow ${expandedNotes.has(note.id) ? 'expanded' : ''}`}>
                      ▼
                    </span>
                  </div>
                </div>
                
                {expandedNotes.has(note.id) && (
                  <div className="note-content">
                    {note.content && (
                      <div className="note-text">
                        {note.content_html ? (
                          <div dangerouslySetInnerHTML={{ __html: note.content_html }} />
                        ) : (
                          <p>{note.content}</p>
                        )}
                      </div>
                    )}
                    
                    {note.audio_clips && note.audio_clips.length > 0 && (
                      <div className="note-audio-clips">
                        <h4>Audio Recordings:</h4>
                        {note.audio_clips.map((clip, index) => {
                          const clipId = `${note.id}-${index}`;
                          const isPlaying = playingAudio === clipId;
                          
                          return (
                            <div key={index} className="audio-clip-item">
                              <div className="audio-clip-header">
                                <span className="audio-clip-name">{clip.name}</span>
                                <div className="audio-clip-actions">
                                  <button 
                                    onClick={() => handleAudioPlay(clipId)}
                                    className={`audio-action-button play-button ${isPlaying ? 'playing' : ''}`}
                                    title={isPlaying ? "Stop audio" : "Play audio"}
                                  >
                                    {isPlaying ? '⏹️' : '▶️'}
                                  </button>
                                  <button 
                                    onClick={() => handleAudioDownload(clip)}
                                    className="audio-action-button download-button"
                                    title="Download audio file (WebM format)"
                                  >
                                    Download Audio
                                  </button>
                                </div>
                              </div>
                              
                              {isPlaying && (
                                <div className="audio-player-container">
                                  <audio 
                                    controls
                                    autoPlay
                                    onEnded={() => setPlayingAudio(null)}
                                    onError={() => setPlayingAudio(null)}
                                    className="inline-audio-player"
                                  >
                                    <source src={clip.file_url} type="audio/webm" />
                                    <source src={clip.file_url} type="audio/mp3" />
                                    <source src={clip.file_url} type="audio/wav" />
                                    Your browser does not support the audio element.
                                  </audio>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Show More/Less button */}
          {hasMoreNotes && (
            <div style={{
              marginTop: '16px',
              textAlign: 'center',
              paddingTop: '16px',
              borderTop: '1px solid #374151'
            }}>
              <button
                onClick={() => setShowAll(!showAll)}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#047857';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#059669';
                }}
              >
                {showAll ? `▲ Show Less` : `▼ Show All ${rehearsalNotes.length} Notes`}
              </button>
            </div>
          )}
        </>
        )}
      </div>
    </div>
  );
};

export default PreviousRehearsalNotes;